/** Formatting helpers. Timestamps from the API are Unix seconds unless stated otherwise. */

const dateFormatter = new Intl.DateTimeFormat("en-GB", {
  day: "numeric", month: "short", year: "numeric",
});
const dateTimeFormatter = new Intl.DateTimeFormat("en-GB", {
  day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
});
const longDateFormatter = new Intl.DateTimeFormat("en-GB", {
  day: "numeric", month: "long", year: "numeric",
});

const toDate = (seconds: number) => new Date(seconds * 1000);

export const formatDate = (seconds: number | null | undefined): string =>
  seconds ? dateFormatter.format(toDate(seconds)) : "—";

export const formatDateTime = (seconds: number | null | undefined): string =>
  seconds ? dateTimeFormatter.format(toDate(seconds)) : "—";

export const formatLongDate = (seconds: number | null | undefined): string =>
  seconds ? longDateFormatter.format(toDate(seconds)) : "—";

/** Milliseconds, which is what the grader stamps its results with. */
export const formatDateTimeMs = (ms: number | null | undefined): string =>
  ms ? dateTimeFormatter.format(new Date(ms)) : "—";

export const isoDate = (seconds: number | null | undefined): string =>
  seconds ? toDate(seconds).toISOString() : "";

export function relativeTime(seconds: number | null | undefined): string {
  if (!seconds) return "never";
  const delta = Math.round(Date.now() / 1000) - seconds;
  if (delta < 45) return "just now";
  if (delta < 90) return "a minute ago";
  if (delta < 3600) return `${Math.round(delta / 60)} minutes ago`;
  if (delta < 7200) return "an hour ago";
  if (delta < 86400) return `${Math.round(delta / 3600)} hours ago`;
  if (delta < 172800) return "yesterday";
  if (delta < 2592000) return `${Math.round(delta / 86400)} days ago`;
  return formatDate(seconds);
}

export function formatMinutes(minutes: number | null | undefined): string {
  if (!minutes || minutes <= 0) return "—";
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (!rest) return `${hours} hr`;
  return `${hours} hr ${rest} min`;
}

/** A 0–1 score as a whole percentage. */
export const formatPercent = (fraction: number | null | undefined): string =>
  fraction === null || fraction === undefined ? "—" : `${Math.round(fraction * 100)}%`;

export function formatBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(bytes < 10240 ? 1 : 0)} kB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** A browser string short enough for a table cell. */
export function describeUserAgent(ua: string | null | undefined): string {
  if (!ua) return "Unknown device";
  const browser = /Edg\//.test(ua) ? "Edge"
    : /OPR\//.test(ua) ? "Opera"
      : /Firefox\//.test(ua) ? "Firefox"
        : /Chrome\//.test(ua) ? "Chrome"
          : /Safari\//.test(ua) ? "Safari"
            : /HeadlessChrome/.test(ua) ? "Headless Chrome"
              : "Browser";
  const platform = /Windows/.test(ua) ? "Windows"
    : /Mac OS X/.test(ua) ? "macOS"
      : /Android/.test(ua) ? "Android"
        : /(iPhone|iPad)/.test(ua) ? "iOS"
          : /Linux/.test(ua) ? "Linux"
            : "";
  return platform ? `${browser} on ${platform}` : browser;
}

/** Splits a base32 TOTP secret into readable groups of four. */
export function chunkSecret(secret: string, size = 4): string[] {
  const out: string[] = [];
  for (let i = 0; i < secret.length; i += size) out.push(secret.slice(i, i + size));
  return out;
}

export const pluralise = (n: number, one: string, many = `${one}s`): string =>
  `${n} ${n === 1 ? one : many}`;

export function fileName(path: string): string {
  const parts = path.split("/").filter(Boolean);
  return parts[parts.length - 1] ?? path;
}

export function parentPath(path: string): string {
  const parts = path.split("/").filter(Boolean);
  parts.pop();
  return `/${parts.join("/")}`;
}
